import { useRef, useCallback } from 'react';
import { CONGESTION_CONFIG } from '../data/seoulSpots.js';

/**
 * 혼잡도 마커 생성 (커스텀 오버레이)
 */
function createCongestionMarker(spot, congestionData, onClick, lang = 'ko') {
  const kakao = window.kakao;
  if (!kakao || !kakao.maps) return null;

  let config = CONGESTION_CONFIG[congestionData?.congestionLevel] || {
    color: '#94a3b8',
    bgColor: '#f1f5f9',
  };

  // 안전/편의시설 전용 색상 오버라이드
  if (spot.category === 'police') {
    config = { color: '#2563eb', bgColor: '#dbeafe' }; // 파란색
  } else if (spot.category === 'hospital') {
    config = { color: '#e11d48', bgColor: '#ffe4e6' }; // 빨간색(Rose)
  } else if (spot.category === 'embassy') {
    config = { color: '#0d9488', bgColor: '#ccfbf1' }; // 청록색(Teal)
  }

  const hasEvents = congestionData?.events && congestionData.events.length > 0;

  const content = document.createElement('div');
  content.id = `marker-${spot.id}`;
  content.style.cssText = `
    position: relative;
    cursor: pointer;
    transition: transform 0.15s ease;
  `;

  const markerStyle = `
    background: ${config.color};
    color: white;
    padding: 6px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    box-shadow: 0 3px 12px ${config.color}55;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Noto Sans KR', sans-serif;
    border: ${hasEvents ? '3px solid #fbbf24' : 'none'};
  `;

  let displayName = lang === 'en' && spot.en ? spot.en : spot.name;
  let flagEmoji = '';
  
  // 대사관 마커인 경우 지도에는 나라 이름만 노출 ("주한 미국 대사관" -> "미국")
  if (spot.category === 'embassy') {
    const countryKo = spot.name.replace('주한 ', '').replace(' 대사관', '');
    const flags = {
      '미국': '🇺🇸', '중국': '🇨🇳', '일본': '🇯🇵', '영국': '🇬🇧', '프랑스': '🇫🇷',
      '독일': '🇩🇪', '캐나다': '🇨🇦', '호주': '🇦🇺', '러시아': '🇷🇺', '베트남': '🇻🇳',
      '태국': '🇹🇭', '인도': '🇮🇳', '스페인': '🇪🇸', '이탈리아': '🇮🇹', '브라질': '🇧🇷',
      '필리핀': '🇵🇭', '스위스': '🇨🇭', '네덜란드': '🇳🇱', '스웨덴': '🇸🇪', '뉴질랜드': '🇳🇿',
      '멕시코': '🇲🇽', '아르헨티나': '🇦🇷', '사우디아라비아': '🇸🇦', '인도네시아': '🇮🇩', '말레이시아': '🇲🇾'
    };
    
    if (flags[countryKo]) {
      flagEmoji = `<span style="font-size:14px; margin-right:2px;">${flags[countryKo]}</span>`;
    }
    
    if (lang === 'ko') displayName = countryKo;
    if (lang === 'en') displayName = displayName.replace(' Embassy', '');
  } else if (spot.category === 'police') {
    flagEmoji = `<span style="font-size:14px; margin-right:2px;">🚨</span>`;
  } else if (spot.category === 'hospital') {
    flagEmoji = `<span style="font-size:14px; margin-right:2px;">🏥</span>`;
  }
  const intlBadge = spot.hasIntlClinic  
    ? `<span style="margin-left:4px; font-size:10px; background:#0284c7; color:white; padding:2px 4px; border-radius:4px; font-weight:normal;">${lang === 'en' ? 'Intl Clinic' : '국제진료'}</span>` 
    : '';

  content.innerHTML = `
    <div style="${markerStyle}">
      ${hasEvents ? '<span style="font-size:14px; margin-right:2px;">🎉</span>' : (flagEmoji ? flagEmoji : '<span style="font-size:10px;">●</span>')}
      ${displayName}
      ${intlBadge}
    </div>
    <div style="
      position: absolute;
      left: 50%;
      bottom: -6px;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid ${hasEvents ? '#fbbf24' : config.color};
    "></div>
  `;

  content.addEventListener('click', () => onClick(spot, congestionData));
  content.addEventListener('mouseenter', () => {
    content.style.transform = 'scale(1.1) translateY(-2px)';
  });
  content.addEventListener('mouseleave', () => {
    content.style.transform = '';
  });

  const position = new kakao.maps.LatLng(spot.lat, spot.lng);

  const overlay = new kakao.maps.CustomOverlay({
    position,
    content,
    yAnchor: 1.3,
    zIndex: 3,
  });

  return overlay;
}

/**
 * 그룹 멤버 마커 생성
 */
function createMemberMarker(member, isMe = false, onClick = null) {
  const kakao = window.kakao;
  if (!kakao || !kakao.maps) return null;

  // 1:1 공유 멤버는 골드 계열, 일반 그룹은 기존 색상
  const isPrivate = member.isPrivateSharing;
  // 1:1 멤버 중 한시적 공유(sharedAtText 있음)는 그레이, 상시 활성은 골드
  const privateColor = member.sharedAtText ? "#94a3b8" : "#f59e0b";
  const color = isMe ? "#3b82f6" : (isPrivate ? privateColor : "#f43f5e");

  // 고유 ID 기반으로 일관된 랜덤 이모지 선택
  const emojis = ['🐶', '🐱', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐣', '🦄', '👦', '👧', '🧑', '👱‍♀️', '👱‍♂️', '👽', '👻', '🤖', '😎', '🥰'];
  const hash = member.uid ? member.uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
  const emoji = emojis[hash % emojis.length];

  const content = document.createElement("div");
  content.style.cssText = `
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  let distanceHtml = "";
  if (!isMe && member.distanceText && member.walkTimeText) {
    distanceHtml = `
      <div style="font-size: 9px; font-weight: normal; margin-top: 2px; opacity: 0.9;">
        ${member.distanceText} • ${member.walkTimeText}
      </div>
    `;
  }

  // 비활성 상태에서 요청에 의한 공유 시 시간 표시
  let timeHtml = "";
  if (member.sharedAtText) {
    timeHtml = `
      <div style="font-size: 8px; font-weight: normal; margin-top: 2px; color: #fbbf24;">
        공유시간: ${member.sharedAtText}
      </div>
    `;
  }

  content.innerHTML = `
    <div style="
      background: ${color};
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 700;
      margin-bottom: 4px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.2);
      white-space: nowrap;
      border: 1.5px solid white;
      text-align: center;
    ">
      ${isMe ? "나" : member.nickname || "친구"}
      ${distanceHtml}
      ${timeHtml}
    </div>
    <div style="
      display: flex;
      justify-content: center;
      align-items: center;
      width: 30px;
      height: 30px;
      background: white;
      border-radius: 50%;
      box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      border: 2.5px solid ${color};
    ">
      <span style="font-size: 16px; transform: translateY(-1px); display: inline-block;">
        ${emoji}
      </span>
    </div>
  `;


  const position = new kakao.maps.LatLng(member.lat, member.lng);
  const overlay = new kakao.maps.CustomOverlay({
    position,
    content,
    yAnchor: 1,
    zIndex: 10, // 멤버 마커를 장소 마커보다 위로
  });

  if (onClick && !isMe) {
    content.style.cursor = 'pointer';
    content.addEventListener('click', () => onClick(member));
  }

  return overlay;
}

/**
 * 카카오맵 훅
 */
export function useKakaoMap(containerRef, options = {}) {
  const mapRef = useRef(null);
  const spotOverlaysRef = useRef([]);
  const memberOverlaysRef = useRef([]);
  const routePolylineRef = useRef(null);

  const initMap = useCallback(() => {
    const kakao = window.kakao;
    if (!containerRef.current || !kakao || !kakao.maps) return;

    const { center = { lat: 37.5326, lng: 126.9947 }, level = 8 } = options;

    const mapOptions = {
      center: new kakao.maps.LatLng(center.lat, center.lng),
      level,
      mapTypeId: kakao.maps.MapTypeId.ROADMAP,
    };

    const map = new kakao.maps.Map(containerRef.current, mapOptions);
    mapRef.current = map;

    return map;
  }, [options]);

  const clearSpotOverlays = useCallback(() => {
    spotOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    spotOverlaysRef.current = [];
  }, []);

  const clearMemberOverlays = useCallback(() => {
    memberOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    memberOverlaysRef.current = [];
  }, []);

  const renderMarkers = useCallback((spots, congestionMap, onMarkerClick, lang = 'ko') => {
    if (!mapRef.current || !window.kakao?.maps) return;
    clearSpotOverlays();

    spots.forEach(spot => {
      const data = congestionMap.get(spot.id);
      const overlay = createCongestionMarker(spot, data, onMarkerClick, lang);
      if (overlay) {
        overlay.setMap(mapRef.current);
        spotOverlaysRef.current.push(overlay);
      }
    });
  }, [clearSpotOverlays]);

  const renderMemberMarkers = useCallback((members, myId, onMemberClick = null) => {
    if (!mapRef.current || !window.kakao?.maps) return;
    clearMemberOverlays();

    members.forEach(member => {
      // 위치 정보가 있는 멤버만 표시
      if (member.lat && member.lng) {
        const overlay = createMemberMarker(member, member.uid === myId, onMemberClick);
        if (overlay) {
          overlay.setMap(mapRef.current);
          memberOverlaysRef.current.push(overlay);
        }
      }
    });
  }, [clearMemberOverlays]);

  const panTo = useCallback((lat, lng) => {
    if (!mapRef.current || !window.kakao?.maps) return;
    const position = new window.kakao.maps.LatLng(lat, lng);
    mapRef.current.panTo(position);
  }, []);

  const setCenter = useCallback((lat, lng) => {
    if (!mapRef.current || !window.kakao?.maps) return;
    const position = new window.kakao.maps.LatLng(lat, lng);
    mapRef.current.setCenter(position);
  }, []);

  const setLevel = useCallback((level) => {
    if (!mapRef.current) return;
    mapRef.current.setLevel(level);
  }, []);

  const getLevel = useCallback(() => {
    return mapRef.current?.getLevel() ?? 8;
  }, []);

  const fitBoundsToMembers = useCallback((members) => {
    if (!mapRef.current || !window.kakao?.maps) return;

    let hasValidLocation = false;
    const bounds = new window.kakao.maps.LatLngBounds();

    members.forEach(member => {
      if (member.lat && member.lng) {
        bounds.extend(new window.kakao.maps.LatLng(member.lat, member.lng));
        hasValidLocation = true;
      }
    });

    if (hasValidLocation) {
      // 너무 과도하게 확대되는 것을 막기 위해 패딩 효과 부여 (보통 bounds 적용 후 level 조정)
      mapRef.current.setBounds(bounds);

      // 혹시 멤버가 1명이거나 너무 가까운 경우 줌 레벨 제한
      const currentLevel = mapRef.current.getLevel();
      if (currentLevel < 4) {
        mapRef.current.setLevel(4);
      }
    }
  }, []);

  const clearRoute = useCallback(() => {
    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
      routePolylineRef.current = null;
    }
  }, []);

  const drawRoute = useCallback((pathCoordinates) => {
    if (!mapRef.current || !window.kakao?.maps) return;
    
    clearRoute();
    
    const linePath = pathCoordinates.map(coord => new window.kakao.maps.LatLng(coord.lat, coord.lng));
    
    const polyline = new window.kakao.maps.Polyline({
      path: linePath,
      strokeWeight: 5,
      strokeColor: '#3b82f6',
      strokeOpacity: 0.8,
      strokeStyle: 'solid'
    });
    
    polyline.setMap(mapRef.current);
    routePolylineRef.current = polyline;
  }, [clearRoute]);

  return {
    mapRef,
    initMap,
    clearSpotOverlays,
    clearMemberOverlays,
    renderMarkers,
    renderMemberMarkers,
    panTo,
    setCenter,
    setLevel,
    getLevel,
    fitBoundsToMembers,
    drawRoute,
    clearRoute
  };
}
